// Tiny global state — fetched once, refreshed when the user changes anything.
// We don't pull in Redux/Zustand; just React state hoisted to <App/> and shared
// via Context, plus a few helpers.

import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setAuthToken } from "./api.js";

const AppCtx = createContext(null);

export function AppProvider({ children }) {
  const [tasks, setTasks]       = useState(null);
  const [settings, setSet]      = useState(null);
  const [meta, setMeta]         = useState(null);
  const [milestones, setMs]     = useState(null);
  const [error, setError]       = useState(null);
  const [toast, setToast]       = useState(null);
  // auth: { loading, required, authenticated }
  const [auth,  setAuth]        = useState({ loading: true, required: false, authenticated: false });

  const refresh = useCallback(async () => {
    try {
      const s = await api.authStatus();
      const required = !!s.authRequired;
      const authed   = !!s.authenticated;
      setAuth({ loading: false, required, authenticated: authed });
      if (required && !authed) {
        // Don't try to load gated data; the login screen handles it.
        setTasks(null); setSet(null); setMeta(null); setMs(null); setError(null);
        return;
      }
      const [t, settings, m, ms] = await Promise.all([
        api.listTasks(), api.getSettings(), api.meta(), api.listMilestones(),
      ]);
      setTasks(t); setSet(settings); setMeta(m); setMs(ms); setError(null);
    } catch (e) {
      setError(e);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // api.js emits this when it sees a 401 (token expired or rejected).
  useEffect(() => {
    const onAuthChange = () => refresh();
    window.addEventListener("nyce-auth-changed", onAuthChange);
    return () => window.removeEventListener("nyce-auth-changed", onAuthChange);
  }, [refresh]);

  const login = useCallback(async (password) => {
    const { token } = await api.authLogin(password);   // throws on 401
    setAuthToken(token);
    await refresh();
  }, [refresh]);

  const logout = useCallback(() => {
    setAuthToken(null);
    setAuth(a => ({ ...a, authenticated: false }));
    setTasks(null); setSet(null); setMeta(null); setMs(null);
  }, []);

  // --- Milestone CRUD with optimistic updates -----------------------------
  const patchMilestone = useCallback(async (id, patch) => {
    setMs(prev => prev && prev.map(m => m.id === id ? { ...m, ...patch } : m));
    try {
      const updated = await api.updateMilestone(id, patch);
      setMs(prev => prev && prev.map(m => m.id === id ? updated : m));
    } catch (e) { setError(e); refresh(); }
  }, [refresh]);

  const addMilestone = useCallback(async (body) => {
    try {
      const created = await api.createMilestone(body);
      setMs(prev => prev ? [...prev, created] : [created]);
      return created;
    } catch (e) { setError(e); }
  }, []);

  const removeMilestone = useCallback(async (id) => {
    setMs(prev => prev && prev.filter(m => m.id !== id));
    // Strip the id from any task.gateDeps locally so arrows disappear immediately.
    setTasks(prev => prev && prev.map(t => {
      const deps = t.gateDeps || [];
      if (!deps.includes(id)) return t;
      return { ...t, gateDeps: deps.filter(x => x !== id) };
    }));
    try {
      await api.deleteMilestone(id);
    } catch (e) { setError(e); refresh(); }
  }, [refresh]);

  // Optimistic helper: apply patch locally for immediate UI feedback,
  // then PUT to backend, then sync with server response.
  const patchTask = useCallback(async (id, patch) => {
    setTasks(prev => prev && prev.map(t => t.id === id ? { ...t, ...patch } : t));
    try {
      const updated = await api.updateTask(id, patch);
      setTasks(prev => prev && prev.map(t => t.id === id ? updated : t));
    } catch (e) {
      setError(e);
      refresh();
    }
  }, [refresh]);

  // Reorder a list of task IDs (must all belong to the same dept). Updates
  // sortOrder in place locally for instant feedback, then commits.
  const reorderTasks = useCallback(async (orderedIds) => {
    const indexOf = new Map(orderedIds.map((id, i) => [id, (i + 1) * 10]));
    setTasks(prev => prev && prev.map(t =>
      indexOf.has(t.id) ? { ...t, sortOrder: indexOf.get(t.id) } : t
    ));
    try {
      await api.reorderTasks(orderedIds);
    } catch (e) { setError(e); refresh(); }
  }, [refresh]);

  const resetTask = useCallback(async (id) => {
    const updated = await api.resetTask(id);
    setTasks(prev => prev && prev.map(t => t.id === id ? updated : t));
    setToast(`Reset task #${updated.briefNo}`);
    setTimeout(() => setToast(null), 2000);
  }, []);

  // --- Task create / archive (soft-delete) / restore ----------------------
  const addTask = useCallback(async (body) => {
    try {
      const created = await api.createTask(body);
      setTasks(prev => prev ? [...prev, created] : [created]);
      return created;
    } catch (e) { setError(e); }
  }, []);

  const archiveTask = useCallback(async (id) => {
    setTasks(prev => prev && prev.filter(t => t.id !== id));   // optimistic remove
    try {
      await api.updateTask(id, { archived: true });
      setToast("Task archived");
      setTimeout(() => setToast(null), 2000);
    } catch (e) { setError(e); refresh(); }
  }, [refresh]);

  const duplicateTask = useCallback(async (id) => {
    try {
      const created = await api.duplicateTask(id);
      setTasks(prev => prev ? [...prev, created] : [created]);
      setToast(`Copied as #${created.briefNo}`);
      setTimeout(() => setToast(null), 2000);
      return created;
    } catch (e) { setError(e); }
  }, []);

  const restoreTask = useCallback(async (id) => {
    try {
      await api.updateTask(id, { archived: false });
      await refresh();   // pulls it back into the live task list
      setToast("Task restored");
      setTimeout(() => setToast(null), 2000);
    } catch (e) { setError(e); }
  }, [refresh]);

  const patchSettings = useCallback(async (patch) => {
    setSet(prev => prev && { ...prev, ...patch });
    try {
      const s = await api.updateSettings(patch);
      setSet(s);
    } catch (e) { setError(e); refresh(); }
  }, [refresh]);

  const resetAll = useCallback(async () => {
    await Promise.all([api.resetAllTasks(), api.resetSettings()]);
    await refresh();
    setToast("Everything reset to defaults");
    setTimeout(() => setToast(null), 2200);
  }, [refresh]);

  return (
    <AppCtx.Provider value={{ tasks, settings, meta, milestones, error, toast, auth,
      login, logout,
      patchTask, resetTask, reorderTasks, patchSettings, resetAll, refresh,
      addTask, archiveTask, restoreTask, duplicateTask,
      patchMilestone, addMilestone, removeMilestone }}>
      {children}
    </AppCtx.Provider>
  );
}

export function useApp() {
  const v = useContext(AppCtx);
  if (!v) throw new Error("useApp() outside <AppProvider>");
  return v;
}

// Hash routing (mirrors the single-file SPA's behaviour)
export function useHashRoute() {
  const [hash, setHash] = useState(window.location.hash || "#/");
  useEffect(() => {
    const onChange = () => setHash(window.location.hash || "#/");
    window.addEventListener("hashchange", onChange);
    return () => window.removeEventListener("hashchange", onChange);
  }, []);
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [hash]);
  return hash;
}

export function parseRoute(hash) {
  const h = (hash || "#/").replace(/^#/, "");
  if (h === "" || h === "/") return { name: "home" };
  let m;
  if ((m = h.match(/^\/dept\/([A-Za-z]+)\/?$/))) return { name: "dept", dept: m[1] };
  if ((m = h.match(/^\/task\/([a-z0-9-]+)\/?$/))) return { name: "task", id: m[1] };
  if (h.startsWith("/settings")) return { name: "settings" };
  return { name: "home" };
}
